import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./AppBar.css";

import useClickOutside from "../hooks/useClickOutside";
import { useTableDropdownLogic } from "../hooks/useTableDropdownLogic";
import {
  TablesDropdown,
  ProfileDropdown,
  TablePopups,
  AppBarButton,
  BackButton,
  CancelSection,
  BookmarkButton,
  EyeIconButton,
  QuestionIconButton,
  TitleSection,
} from "./AppBarComponents";
import { buildPermissionContext, PERMISSIONS } from "../util/permissions";

const AppBar = ({
  title = "",
  showTablesDropdown = true,
  showHideFromLiveMode = false,
  hideFromLiveMode = false,
  onHideFromLiveModeChange = null,
  disableHideFromLiveMode = false,
  buttons = [],
  buttonsBeforeTitle = [],
  buttonBeforeTitle = null,
  buttonAfterTitle = null,
  showBackButton = true,
  moveToDirectoryScreen = false,
  showProfileButton = true,
  showColorTitle = true,
  showBlackTitle = false,
  showEyeIcon = false,
  showQuestionIcon = false,
  /** When true, QuestionIconButton is shown read-only (not clickable), e.g. Directory classic bar. */
  questionIconDummy = false,
  onQuestionIconClick = null,
  showBookmark = false,
  onBookmarkClick = null,
  /** "bookmark" = open live mode; "edit" = back to edit (Products live bar parity). */
  bookmarkVariant = "bookmark",
  subheading = "",
  navigateTwoPagesBack = false,
  subheadingAfterTitle = "",
  disableBarButtons = false,
  showCancelButton = false,
  showCreateCopyCheckbox = false,
  subTitleAboveTitle = "",
  createCopy = false,
  onCreateCopyChange = () => {},
  versionHistoryHandler = null,
  fromVersionHistory,
  onBack = null,
  previewData = null,
  onPreviewClick = null,
  previewIconDisabled = false,
  previewIconTitle = "",
  isSticky = true,
  editButton = null,
  onTablesDropdownOpen = null,
  titleTextColor = null,
  titleFontSize = "20px",
  cancelButtonMarginTop = null,
  onCancel = null,
}) => {

  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const handleCancel = () => {
    if (typeof onCancel === "function") {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const {
    showTablesDropdown: showDropdown, // Remap to match existing code
    setShowTablesDropdown: setShowDropdown, // Remap to match existing code
    tablesDropdownRef: dropdownRef, // Remap to match existing code
    showTablePopup,
    handleDrpdnClick,
    closeTablePopup: closePopup, // Remap to match existing code
  } = useTableDropdownLogic();

  const profileRef = useRef();

  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canAccessTables =
    permissionCtx.permissions?.[PERMISSIONS.EDIT] === true;

  useClickOutside(profileRef, () => setShowProfileDropdown(false));


  // Close additional info popup when tables dropdown opens
  useEffect(() => {
    if (showDropdown && onTablesDropdownOpen) {
      onTablesDropdownOpen();
    }
  }, [showDropdown, onTablesDropdownOpen]);

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else if (moveToDirectoryScreen) {
      navigate("/");
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <header className={`appbar ${!isSticky ? "appbar-non-sticky" : ""}`}>
        {showCancelButton && (
          <CancelSection
            onCancel={handleCancel}
            showCreateCopyCheckbox={showCreateCopyCheckbox}
            createCopy={createCopy}
            onCreateCopyChange={onCreateCopyChange}
            cancelButtonMarginTop={cancelButtonMarginTop}
          />
        )}

        {showBackButton && <BackButton onBack={handleBackClick} />}

        {showBookmark && (
          <BookmarkButton
            onClick={onBookmarkClick}
            variant={bookmarkVariant}
          />
        )}

        {showTablesDropdown && (
          <TablesDropdown
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
            disableBarButtons={disableBarButtons || !canAccessTables}
            handleDrpdnClick={handleDrpdnClick}
            versionHistoryHandler={versionHistoryHandler}
            dropdownRef={dropdownRef}
            isAccessible={canAccessTables}
          />
        )}

        {showHideFromLiveMode && (
          <label
            className={`appbar-hide-from-live${disableHideFromLiveMode ? " appbar-hide-from-live--disabled" : ""}`}
          >
            <input
              type="checkbox"
              checked={!!hideFromLiveMode}
              disabled={disableHideFromLiveMode}
              onChange={(e) => {
                if (typeof onHideFromLiveModeChange === "function") {
                  onHideFromLiveModeChange(e.target.checked);
                }
              }}
            />
            <span>Hide From Live Mode</span>
          </label>
        )}

        <p style={{ marginLeft: "10px", fontSize: "12px" }}>{subheading}</p>

        {buttonsBeforeTitle.map((button, index) => (
          <AppBarButton
            key={index}
            button={button}
            index={index}
            disableBarButtons={disableBarButtons}
          />
        ))}
        <br />
        {/* Title and Subtitle Stacked Vertically */}
        <TitleSection
          title={title}
          subTitleAboveTitle={subTitleAboveTitle}
          showColorTitle={showColorTitle}
          showBlackTitle={showBlackTitle}
          editButton={editButton}
          buttonBeforeTitle={buttonBeforeTitle}
          buttonAfterTitle={buttonAfterTitle}
          titleTextColor={titleTextColor}
          titleFontSize={titleFontSize}
        />

        <div className="appbar-buttons">
          {/* ✅ Subheading after title */}
          {subheadingAfterTitle && !fromVersionHistory && (
            <p
              style={{
                marginLeft: "10px",
                fontSize: "12px",
                maxWidth: "170px",
              }}
            >
              {subheadingAfterTitle}
            </p>
          )}
          {subheadingAfterTitle && fromVersionHistory && (
            <p
              style={{
                marginLeft: "10px",
                fontSize: "12px",
                color: "#0D6EFD",
                fontWeight: "500",
                maxWidth: "170px",
              }}
            >
              {subheadingAfterTitle}
            </p>
          )}
          {buttons.map((button, index) => (
            <AppBarButton
              key={index}
              button={button}
              index={index}
              disableBarButtons={disableBarButtons}
            />
          ))}

          {showEyeIcon && (
            <EyeIconButton
              onPreviewClick={onPreviewClick}
              previewData={previewData}
              disabled={previewIconDisabled}
              title={previewIconTitle}
            />
          )}

          {showQuestionIcon && (
            <QuestionIconButton
              readOnly={questionIconDummy}
              onClick={onQuestionIconClick}
            />
          )}

          <ProfileDropdown
            showProfileDropdown={showProfileDropdown}
            setShowProfileDropdown={setShowProfileDropdown}
            profileRef={profileRef}
            showProfileButton={showProfileButton}
          />
        </div>
      </header>

      <TablePopups showTablePopup={showTablePopup} closePopup={closePopup} />
    </>
  );
};

export default AppBar;
